import { gsap } from "gsap";
import { Howler } from 'howler';
import { Dictionary } from "../../util/collection/Dictionary";
import { DeviceUtil } from "../../util/device/DeviceUtil";
import { SoundProperties } from "../control/properties/SoundProperties";
import { SoundChannelType } from "../controller/type/SoundChannelType";
import { AssetManager } from "./AssetManager";
import { SoundChannel } from "./data/SoundChannel";
/*
 * SoundManager.ts
 */
export class SoundManager {
    constructor() {
        if (SoundManager.instance) {
            throw new Error("Audio Manager Singleton Already Initialised");
        }
        SoundManager.instance = this;
        this.create();
        this.addListeners();
    }
    create() {
        this.createVolume();
        this.createRegisteredSounds();
        this.createAssetManager();
        this.createChannels();
    }
    addListeners() {
        if (DeviceUtil.isTouch()) {
            this.touchHandler = this.handleUnlock.bind(this);
            document.addEventListener("touchstart", this.touchHandler, true);
            document.addEventListener("touchend", this.touchHandler, true);
        }
    }
    removeListeners() {
        document.removeEventListener("touchstart", this.touchHandler, true);
        document.removeEventListener("touchend", this.touchHandler, true);
    }
    handleUnlock() {
        let context = Howler.ctx;
        if (context) {
            context.resume();
            this.removeListeners();
            this.touchHandler = null;
        }
    }
    createVolume() {
        this.soundEnabled = true;
        this.volume = 0.5;
    }
    createRegisteredSounds() {
        this.registeredSounds = new Dictionary();
    }
    createAssetManager() {
        this.assetManager = AssetManager.getInstance();
    }
    createChannels() {
        this.channels = new Dictionary();
    }
    preload(path, completeHandler) {
        let soundProperties;
        if (path.getSoundChannel() === null) {
            console.warn(path.getId() + " sound channel not found in Config.xml, reverting to sound constants");
            soundProperties = this.getRegisteredSound(path.getId());
            if (!soundProperties || soundProperties?.channel === null) {
                console.warn(path.getId() + " sound properties not found in Sound Constants, creating default properties");
                soundProperties = this.registerAndReturnSoundsFromConfigXML(path);
            }
        }
        else {
            soundProperties = this.registerAndReturnSoundsFromConfigXML(path);
        }
        if (soundProperties) {
            let audioChannel = this.getChannel(soundProperties.channel);
            audioChannel.preloadSound(path.getUrl(), soundProperties, completeHandler);
        }
        else {
            console.log("Failed to preload sound: " + path.getUrl());
        }
    }
    unload(path) {
        let soundProperties = this.getRegisteredSound(path.getId());
        if (soundProperties) {
            let audioChannel = this.getChannel(soundProperties.channel);
            audioChannel.unloadSound(soundProperties);
        }
    }
    registerSound(properties) {
        this.registeredSounds.setValue(properties.name, properties);
    }
    play(properties) {
        let path = this.assetManager.getSound(properties.name, properties.component);
        let sound;
        if (path) {
            sound = this.playUrl(path.getUrl(), properties);
        }
        return sound;
    }
    playUrl(url, properties) {
        return this.playSound(url, properties);
    }
    stop(properties) {
        this.stopSound(properties);
    }
    playSound(url, properties) {
        let channel = this.getChannel(properties.channel);
        return channel.playSound(url, properties);
    }
    stopSound(properties) {
        if (this.channels.containsKey(properties.channel)) {
            let channel = this.channels.getValue(properties.channel);
            channel.stopSound(properties);
        }
    }
    mute() {
        for (let channel of this.channels.getValues()) {
            channel.mute();
        }
    }
    unmute() {
        for (let channel of this.channels.getValues()) {
            channel.unmute();
        }
    }
    muteChannel(name) {
        let channel = this.getChannel(name);
        channel.mute();
    }
    unmuteChannel(name) {
        if (this.soundEnabled) {
            let channel = this.getChannel(name);
            channel.unmute();
        }
    }
    enableSound() {
        this.soundEnabled = true;
    }
    disableSound() {
        this.soundEnabled = false;
    }
    setChannelVolume(name, volume) {
        let channel = this.getChannel(name);
        channel.setVolume(volume);
    }
    fadeSoundTo(properties, toVolume = 0, durationMs = 1000) {
        let channel = this.getChannel(properties.channel);
        channel.fadeSound(properties, toVolume, durationMs);
    }
    fadeChannelTo(channelName, toVolume = 0, durationMs = 1000) {
        let channel = this.getChannel(channelName);
        let volume = { value: channel.getVolume() };
        return gsap.timeline().to(volume, {
            value: toVolume,
            onUpdate: () => {
                channel.setVolume(volume.value);
            }
        });
    }
    getChannel(name) {
        let channel;
        if (this.channels.containsKey(name)) {
            channel = this.channels.getValue(name);
        }
        else {
            channel = new SoundChannel(this.volume);
            this.channels.setValue(name, channel);
        }
        return channel;
    }
    getRegisteredSound(id) {
        return this.registeredSounds.getValue(id);
    }
    static getInstance() {
        if (SoundManager.instance === null) {
            SoundManager.instance = new SoundManager();
        }
        return SoundManager.instance;
    }
    isMuted() {
        let muted = true;
        for (let channel of this.channels.getValues()) {
            if (!channel.isMuted()) {
                muted = false;
                break;
            }
        }
        return muted;
    }
    static createSoundProperties(name, component, channel, loop = false) {
        const soundProperties = new SoundProperties(name, component, channel, loop);
        SoundManager.getInstance().registerSound(soundProperties);
        return soundProperties;
    }
    registerAndReturnSoundsFromConfigXML(path) {
        let soundProperties = new SoundProperties(path.getId(), "Game", path?.getSoundChannel() || SoundChannelType.EFFECTS, path?.isLoop() || false);
        this.registerSound(soundProperties);
        return soundProperties;
    }
}
SoundManager.instance = null;
//# sourceMappingURL=SoundManager.js.map